import { z } from "zod";
import moment from "moment";
import { hasMultipleProductFormats } from "./workSpecificationUtils";
import { getAge } from "./getAge";
import { normalizeWebUrl } from "./urlUtils";
var ISBN_MIN_DATE = moment("1989-01-01", "YYYY-MM-DD");
var PARTIAL_DATE_FORMATS = [
    "DD.MM.YYYY",
    "DD/MM/YYYY",
    "MM.YYYY",
    "MM/YYYY",
    "YYYY",
    "YYYY-MM-DD",
];
/**
 * Parses a partial date string (DD.MM.YYYY, MM.YYYY, YYYY, etc.) and returns
 * a valid moment object, or an invalid moment if the input doesn't match any format.
 */
var parsePartialDate = function (dateStr) {
    var trimmed = dateStr.trim();
    var monthDotMatch = trimmed.match(/^(\d{1,2})\.(\d{4})$/);
    var monthSlashMatch = trimmed.match(/^(\d{1,2})\/(\d{4})$/);
    var fullDotMatch = trimmed.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
    var fullSlashMatch = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    var monthValue = null;
    if (monthDotMatch) {
        monthValue = parseInt(monthDotMatch[1], 10);
    }
    else if (monthSlashMatch) {
        monthValue = parseInt(monthSlashMatch[1], 10);
    }
    else if (fullDotMatch) {
        monthValue = parseInt(fullDotMatch[2], 10);
    }
    else if (fullSlashMatch) {
        monthValue = parseInt(fullSlashMatch[2], 10);
    }
    if (monthValue !== null && (monthValue < 1 || monthValue > 12)) {
        return moment.invalid();
    }
    for (var _i = 0, PARTIAL_DATE_FORMATS_1 = PARTIAL_DATE_FORMATS; _i < PARTIAL_DATE_FORMATS_1.length; _i++) {
        var fmt = PARTIAL_DATE_FORMATS_1[_i];
        var d = moment(trimmed, fmt, true);
        if (d.isValid()) {
            return d;
        }
    }
    return moment.invalid();
};
var MAX_TEXT_LENGTH = 255;
var maxLengthMessage = function (formatMessage) { return ({
    message: formatMessage({ id: "ui-br-publishers.publishers.validation.maxLength" }, { max: MAX_TEXT_LENGTH }),
}); };
var createContactSchema = function (formatMessage) {
    return z
        .object({
        // Identity of an existing contact; declared so zod does not strip it on submit.
        id: z.string().optional(),
        type: z.union([
            z.literal(""),
            z.enum(["EMAIL", "WEB", "PHONE", "DATA_BOX"]),
        ]),
        value: z.string().max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        info: z
            .string()
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
            .optional(),
    })
        .superRefine(function (contact, ctx) {
        if (contact.type === "" && contact.value === "")
            return;
        if (contact.type !== "" && contact.value === "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.contact.value",
                }),
                path: ["value"],
            });
            return;
        }
        if (contact.type === "" && contact.value !== "") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.contact.type",
                }),
                path: ["type"],
            });
            return;
        }
        if (contact.type === "EMAIL") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contact.value)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.email",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === "WEB") {
            // a web address without a scheme (www.nakladatel.cz) is saved with https:// (Tools 75665)
            var normalized = normalizeWebUrl(contact.value);
            if (normalized === null) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.web",
                    }),
                    path: ["value"],
                });
            }
            else if (contact.value !== normalized) {
                contact.value = normalized;
            }
        }
        else if (contact.type === "PHONE") {
            var input = contact.value.trim();
            var hasPlus = input.startsWith("+");
            var digitsOnly = input.replace(/\D/g, "");
            var hasInvalidChars = /[a-zA-Z]/.test(input);
            if (hasInvalidChars) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
            else if (digitsOnly.length < 9) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
            else if (digitsOnly.length === 9) {
                var formatted = "".concat(digitsOnly.slice(0, 3), " ").concat(digitsOnly.slice(3, 6), " ").concat(digitsOnly.slice(6));
                if (contact.value !== formatted) {
                    contact.value = formatted;
                }
            }
            else if (hasPlus && digitsOnly.length >= 10) {
                if (digitsOnly.length > 15) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.contact.phone.e164",
                        }),
                        path: ["value"],
                    });
                }
                else {
                    var normalized = "+".concat(digitsOnly);
                    if (contact.value !== normalized) {
                        contact.value = normalized;
                    }
                }
            }
            else {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.phone",
                    }),
                    path: ["value"],
                });
            }
        }
        else if (contact.type === "DATA_BOX") {
            if (contact.value.length < 6 || contact.value.length > 12) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.contact.dataBox",
                    }),
                    path: ["value"],
                });
            }
        }
    });
};
var createAddressSchema = function (formatMessage) {
    return z.object({
        addressCode: z.preprocess(function (val) { return (typeof val === "string" && val.trim() === "" ? "" : val); }, z
            .string()
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
            .optional()
            .default("")),
        street: z
            .string()
            .min(1, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.address.street",
            }),
        })
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        municipality: z
            .string()
            .min(1, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.address.municipality",
            }),
        })
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        postalCode: z
            .string()
            .min(1, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.address.postalCode",
            }),
        })
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        region: z.object({
            id: z.string().min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.address.region",
                }),
            }),
            name: z.string().optional(),
        }),
    });
};
export var createPublisherSchema = function (formatMessage, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.validateWorkSpec, validateWorkSpec = _c === void 0 ? true : _c, _d = _b.restoreReactivatesRegistrations, restoreReactivatesRegistrations = _d === void 0 ? false : _d, _e = _b.singleFormatRequired, singleFormatRequired = _e === void 0 ? false : _e, _f = _b.restoreOneOffAsContinuous, restoreOneOffAsContinuous = _f === void 0 ? false : _f, _g = _b.validateGuarantorContacts, validateGuarantorContacts = _g === void 0 ? true : _g;
    return z
        .object({
        legalForm: z.enum(["LEGAL", "NATURAL", "UNKNOWN"], {
            required_error: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.legalForm",
            }),
        }),
        registrations: z
            .array(z.object({
            id: z.string().optional(),
            registrationAgency: z
                .enum(["ISBN", "ISMN", ""], {
                required_error: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.registrationAgency",
                }),
            })
                .refine(function (val) { return val !== ""; }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.registrationAgency",
                }),
            }),
            registrationIntent: z
                .enum(["ONE_OFF", "CONTINUOUS", ""], {
                required_error: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.registrationIntent",
                }),
            })
                .refine(function (val) { return val !== ""; }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.registrationIntent",
                }),
            }),
            registrationTime: z.string().optional(),
            terminationTime: z.string().optional(),
        }))
            .optional(),
        version: z.number().optional(),
        id: z.string().optional(),
        closeTime: z.string().optional(),
        internalComment: z.string().optional(),
        nameSuffix: z
            .string()
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
            .optional(),
        qualifier: z
            .string()
            .max(MAX_TEXT_LENGTH, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.qualifier",
            }),
        })
            .optional(),
        name: z
            .string()
            .min(1, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.name",
            }),
        })
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        status: z.enum(["REQUEST", "REJECTED", "ACTIVE", "CLOSING", "CLOSED"], {
            required_error: formatMessage({
                id: "ui-br-publishers.validation.zod.required",
            }),
        }),
        companyNumber: z.preprocess(function (arg) { return (typeof arg === "string" ? arg.trim() : arg); }, z
            .union([
            z.literal(""),
            z
                .string()
                .min(8, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.companyNumber",
                }),
            })
                .max(8, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.companyNumber",
                }),
            }),
        ])
            .optional()),
        taxNumber: z
            .union([
            z.literal(""),
            z.string().refine(function (val) { return /^[A-Za-z]{2}\d{2,13}$/.test(val); }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.taxNumber",
                }),
            }),
        ])
            .optional(),
        birthDate: z
            .union([
            z.literal(""),
            z
                .string()
                .refine(function (val) { return moment(val, "DD/MM/YYYY", true).isValid(); }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.birthDate.format",
                }),
            }),
        ])
            .optional()
            .transform(function (val) {
            return !val || val === ""
                ? ""
                : moment(val, "DD/MM/YYYY").format("YYYY-MM-DD");
        }),
        mainAddressPublishConsent: z.boolean().default(true),
        mailingAddressPublishConsent: z.boolean().default(true).optional(),
        mainAddress: createAddressSchema(formatMessage),
        mailingAddress: z
            .object({
            street: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            postalCode: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            municipality: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            region: z
                .object({
                id: z.string().optional(),
                name: z.string().optional(),
            })
                .optional(),
        })
            .optional()
            .transform(function (value) {
            if (!value)
                return undefined;
            var street = value.street, postalCode = value.postalCode, municipality = value.municipality, region = value.region;
            var regionId = (region === null || region === void 0 ? void 0 : region.id) || "";
            if ((!street || !street.trim()) &&
                (!postalCode || !postalCode.trim()) &&
                (!municipality || !municipality.trim()) &&
                (!regionId || !regionId.trim())) {
                return undefined;
            }
            return {
                street: street || "",
                postalCode: postalCode || "",
                municipality: municipality || "",
                region: {
                    id: (region === null || region === void 0 ? void 0 : region.id) || "",
                    name: (region === null || region === void 0 ? void 0 : region.name) || "",
                },
            };
        }),
        contacts: z.array(createContactSchema(formatMessage)).optional(),
        alternativeNames: z
            .array(z.object({
            name: z
                .string()
                .min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.alternativeNames.name",
                }),
            })
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
            nameSuffix: z
                .string()
                .max(MAX_TEXT_LENGTH, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.alternativeNames.nameSuffix",
                }),
            })
                .optional(),
            qualifier: z
                .string()
                .max(MAX_TEXT_LENGTH, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.alternativeNames.qualifier",
                }),
            })
                .optional(),
            type: z
                .enum([
                "PREVIOUS",
                "IMPRINT",
                "ABBREVIATION",
                "ORGANIZATION_UNIT",
                "FOREIGN_LANGUAGE",
            ])
                .optional(),
            nonPublic: z.boolean().default(false),
        }))
            .optional(),
        guarantors: z
            .array(z.object({
            // Identity of an existing guarantor (RECEK-75394). zod strips undeclared keys, so without these
            // the backend received every guarantor as new and re-created it (new id + new internal user)
            // on each save. New guarantors simply omit them.
            id: z.string().optional(),
            userId: z.string().optional(),
            source: z.enum(["USERS"]).optional(),
            sourceId: z.string().optional(),
            agencies: z.array(z.enum(["ISBN", "ISMN"])).optional(),
            firstName: z
                .string()
                .min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.guarantors.firstName",
                }),
            })
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
            lastName: z
                .string()
                .min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.guarantors.lastName",
                }),
            })
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
            email: z
                .string()
                .min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.guarantors.email",
                }),
            })
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .refine(function (val) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val); }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.guarantors.email.format",
                }),
            }),
            roles: z
                .array(z.enum(["PublisherAdmin", "PublisherGuarantor"]))
                .default([])
                .refine(function (arr) { return arr.length >= 1; }, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.guarantors.roles",
                }),
            }),
            contacts: validateGuarantorContacts
                ? z.array(createContactSchema(formatMessage)).optional()
                : // not rendered by the form: pass through unvalidated and unstripped
                    z.array(z.custom()).optional(),
            titlesAfter: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            titlesBefore: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
        }))
            .optional(),
        workSpecification: z
            .object({
            title: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            subtitle: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            volumeNumber: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            partTitle: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            authors: z
                .array(
            // an empty name is omitted by the backend (non_empty JSON), so a stored author row
            // may lack it; it must not block the save as a hidden "Required" (Tools 75665)
            z.object({
                firstName: z
                    .string()
                    .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                    .default(""),
                lastName: z
                    .string()
                    .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                    .default(""),
                // NK authority the author was picked from (DB-804)
                personId: z.string().optional(),
                sourceId: z.string().optional(),
            }))
                .optional(),
            publicationDate: z.string().optional(),
            editionNumber: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            coEditionWith: z
                .string()
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                .optional(),
            publicationForm: z
                .union([
                z.literal(""),
                z.enum(["PRINT", "EBOOK", "DATA_CARRIER", "AUDIOBOOK"]),
            ])
                .optional(),
            publicationType: z
                .object({
                printedPublications: z.preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string())),
            })
                .optional(),
            eBookFormats: z
                .preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string()))
                .optional(),
            otherFormats: z
                .preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.object({
                format: z
                    .string()
                    .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage))
                    .default(""),
            })))
                .optional(),
            dataCarrierVersions: z
                .preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string()))
                .optional(),
            audioBookFormats: z
                .preprocess(function (val) { return (Array.isArray(val) ? val : []); }, z.array(z.string()))
                .optional(),
        })
            .optional(),
    })
        .superRefine(function (data, ctx) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (data.mailingAddress) {
            var _h = data.mailingAddress, street = _h.street, postalCode = _h.postalCode, municipality = _h.municipality, region = _h.region;
            if (!street.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.address.street",
                    }),
                    path: ["mailingAddress", "street"],
                });
            }
            if (!/^\d{3} ?\d{2}$/.test(postalCode)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.address.postalCode",
                    }),
                    path: ["mailingAddress", "postalCode"],
                });
            }
            if (!municipality.trim()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.address.municipality",
                    }),
                    path: ["mailingAddress", "municipality"],
                });
            }
            if (!(region === null || region === void 0 ? void 0 : region.id.trim())) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.address.region",
                    }),
                    path: ["mailingAddress", "region", "id"],
                });
            }
        }
        if (data.legalForm === "NATURAL") {
            var hasBirthDate = ((_a = data.birthDate) === null || _a === void 0 ? void 0 : _a.trim()) !== "";
            var hasIco = ((_b = data.companyNumber) === null || _b === void 0 ? void 0 : _b.trim()) !== "";
            var hasDic = ((_c = data.taxNumber) === null || _c === void 0 ? void 0 : _c.trim()) !== "";
            if (!hasBirthDate && !hasIco && !hasDic) {
                var message = formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.legalForm.natural",
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["birthDate"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["companyNumber"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["taxNumber"],
                });
            }
        }
        else if (data.legalForm === "LEGAL") {
            var hasIco = ((_d = data.companyNumber) === null || _d === void 0 ? void 0 : _d.trim()) !== "";
            var hasDic = ((_e = data.taxNumber) === null || _e === void 0 ? void 0 : _e.trim()) !== "";
            if (!hasIco && !hasDic) {
                var message = formatMessage({
                    id: "ui-br-publishers.publishers.validation.general.legalForm.legal",
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["companyNumber"],
                });
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: message,
                    path: ["taxNumber"],
                });
            }
        }
        if (data.legalForm === "NATURAL" &&
            data.birthDate &&
            data.birthDate.trim() !== "") {
            var age = getAge(data.birthDate);
            if (age < 18) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.general.birthDate.age",
                    }, { age: age }),
                    path: ["birthDate"],
                });
            }
        }
        var hasActiveOneOffRegistration = (_f = data.registrations) === null || _f === void 0 ? void 0 : _f.some(function (registration) {
            return registration.registrationIntent === "ONE_OFF" &&
                (!registration.terminationTime ||
                    (restoreReactivatesRegistrations &&
                        !restoreOneOffAsContinuous &&
                        !!data.closeTime &&
                        registration.terminationTime === data.closeTime));
        });
        if (validateWorkSpec && hasActiveOneOffRegistration) {
            var ws = data.workSpecification;
            if (!ws) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: formatMessage({
                        id: "ui-br-publishers.publishers.validation.workSpec.required",
                        defaultMessage: "Work specification is required for one-time assignment.",
                    }),
                    path: ["workSpecification"],
                });
            }
            else {
                if (!ws.title || ws.title.trim() === "") {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.title",
                        }),
                        path: ["workSpecification", "title"],
                    });
                }
                // the publication date is optional (it is not required when creating
                // a title/product in the agency module), but when filled it must be valid
                if (ws.publicationDate && ws.publicationDate.trim() !== "") {
                    var parsedDate = parsePartialDate(ws.publicationDate);
                    if (!parsedDate.isValid()) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: formatMessage({
                                id: "ui-br-publishers.publishers.validation.workSpec.publicationDate.format",
                            }),
                            path: ["workSpecification", "publicationDate"],
                        });
                    }
                    else if (parsedDate.isBefore(ISBN_MIN_DATE)) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: formatMessage({
                                id: "ui-br-publishers.publishers.validation.workSpec.publicationDate.min",
                            }),
                            path: ["workSpecification", "publicationDate"],
                        });
                    }
                    else if (parsedDate.year() > 2100) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            message: formatMessage({
                                id: "ui-br-publishers.publishers.validation.workSpec.publicationDate.max",
                            }),
                            path: ["workSpecification", "publicationDate"],
                        });
                    }
                }
                // at least one product format is needed so the agency can create the
                // product of the one-off title
                if (!ws.publicationForm) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.publicationForm",
                        }),
                        path: ["workSpecification", "publicationForm"],
                    });
                }
                if (ws.publicationForm === "PRINT" &&
                    (!((_g = ws.publicationType) === null || _g === void 0 ? void 0 : _g.printedPublications) ||
                        ws.publicationType.printedPublications.length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.printedPublications",
                        }),
                        path: [
                            "workSpecification",
                            "publicationType",
                            "printedPublications",
                        ],
                    });
                }
                if (ws.publicationForm === "EBOOK" &&
                    (!ws.eBookFormats || ws.eBookFormats.length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.eBookFormats",
                        }),
                        path: ["workSpecification", "eBookFormats"],
                    });
                }
                if (singleFormatRequired && hasMultipleProductFormats(ws)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.error.workSpec.multipleFormats",
                        }),
                        path: ["workSpecification", "publicationForm"],
                    });
                }
                if (ws.publicationForm === "DATA_CARRIER" &&
                    (!ws.dataCarrierVersions || ws.dataCarrierVersions.length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.dataCarrierVersions",
                        }),
                        path: ["workSpecification", "dataCarrierVersions"],
                    });
                }
                if (ws.publicationForm === "AUDIOBOOK" &&
                    (!ws.audioBookFormats || ws.audioBookFormats.length === 0)) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        message: formatMessage({
                            id: "ui-br-publishers.publishers.validation.workSpec.audioBookFormats",
                        }),
                        path: ["workSpecification", "audioBookFormats"],
                    });
                }
            }
        }
    });
};
export var createAresSchema = function (formatMessage) {
    return z.object({
        name: z
            .string()
            .min(1, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.name",
            }),
        })
            .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        mainAddress: createAddressSchema(formatMessage),
        companyNumber: z
            .string()
            .optional()
            .refine(function (val) { return !val || /^\d{8}$/.test(val); }, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.companyNumber",
            }),
        }),
        taxNumber: z
            .string()
            .optional()
            .refine(function (val) {
            return !val || (val.length >= 2 && val.length <= 13 && /^\d+$/.test(val));
        }, {
            message: formatMessage({
                id: "ui-br-publishers.publishers.validation.general.taxNumber",
            }),
        }),
        alternativeNames: z
            .array(z.object({
            name: z
                .string()
                .min(1, {
                message: formatMessage({
                    id: "ui-br-publishers.publishers.validation.alternativeNames.name",
                }),
            })
                .max(MAX_TEXT_LENGTH, maxLengthMessage(formatMessage)),
        }))
            .optional(),
    });
};
/**
 * Czech messages for zod's built-in issues (a missing value, a wrong type, a value outside an
 * enum, ...), which zod would otherwise report in raw English ("Required", "Invalid input").
 * Pass it as the contextual error map of a parse, e.g. `zodResolver(schema, { errorMap })`;
 * messages defined on the schema itself keep precedence.
 *
 * @param formatMessage the react-intl message formatter
 * @return a zod error map
 */
export var createZodErrorMap = function (formatMessage) {
    return function (issue) {
        var required = {
            message: formatMessage({
                id: "ui-br-publishers.validation.zod.required",
            }),
        };
        var invalidValue = {
            message: formatMessage({
                id: "ui-br-publishers.validation.zod.invalidValue",
            }),
        };
        switch (issue.code) {
            case z.ZodIssueCode.invalid_type:
                return issue.received === z.ZodParsedType.undefined ||
                    issue.received === z.ZodParsedType.null
                    ? required
                    : invalidValue;
            case z.ZodIssueCode.too_small:
                return (issue.type === "string" || issue.type === "array") &&
                    Number(issue.minimum) <= 1
                    ? required
                    : invalidValue;
            case z.ZodIssueCode.too_big:
                return issue.type === "string"
                    ? {
                        message: formatMessage({ id: "ui-br-publishers.publishers.validation.maxLength" }, { max: Number(issue.maximum) }),
                    }
                    : invalidValue;
            default:
                return invalidValue;
        }
    };
};
export var validatePublisher = function (data, formatMessage) { return createPublisherSchema(formatMessage).safeParse(data); };
export var validateAres = function (data, formatMessage) {
    return createAresSchema(formatMessage).safeParse(data);
};
